import { useCallback, useEffect, useRef, useState } from 'react'
import {
  NotificationSettings,
  NotificationSettingsPatch,
  getNotificationSettings,
  updateNotificationSettings,
} from '../../services/notificationSettingsService'

/**
 * Hook de preferencias de notificacion.
 *
 * Pattern (identico al `deleteById` optimista de `useNotifications`):
 *   1. Guardamos snapshot del state previo.
 *   2. Aplicamos el patch localmente ANTES del round-trip.
 *   3. Si el PATCH falla, restauramos el snapshot y propagamos el error.
 *
 * Motivacion: los toggles deben sentirse instantaneos; latencia de red
 * (200-800ms) percibida como bug si el switch "titubea".
 *
 * `refetch` queda expuesto por si un consumidor (ej. un WebSocket futuro
 * de "settings changed en otro dispositivo") necesita resincronizar.
 */
export interface UseNotificationSettingsResult {
  settings: NotificationSettings | null
  loading: boolean
  error: Error | null
  save: (patch: NotificationSettingsPatch) => Promise<void>
  refetch: () => Promise<void>
}

export const useNotificationSettings = (): UseNotificationSettingsResult => {
  const [settings, setSettings] = useState<NotificationSettings | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const isMountedRef = useRef<boolean>(true)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const fresh = await getNotificationSettings()
      if (isMountedRef.current) setSettings(fresh)
    } catch (err) {
      if (isMountedRef.current) setError(err as Error)
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    fetchData()
    return () => {
      isMountedRef.current = false
    }
  }, [fetchData])

  const save = useCallback(
    async (patch: NotificationSettingsPatch): Promise<void> => {
      // Snapshot previo para rollback. Si aun no hay settings cargados,
      // no aplicamos optimismo: dejamos que el servidor sea la fuente.
      const previous = settings
      if (previous) {
        setSettings({ ...previous, ...patch })
      }

      try {
        const fresh = await updateNotificationSettings(patch)
        if (isMountedRef.current) setSettings(fresh)
      } catch (err) {
        if (isMountedRef.current && previous) {
          setSettings(previous)
        }
        throw err
      }
    },
    [settings]
  )

  return { settings, loading, error, save, refetch: fetchData }
}
